import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import './FileUpload.css';

/**
 * A functional component that provides file upload functionality using drag-and-drop interaction.
 * It supports specific file formats and allows selecting a file with size constraints.
 *
 * @param {Object} props - The properties object.
 * @param {Function} props.onFileSelect - Callback function triggered when a file is selected. It receives the selected file object as an argument.
 * @param {Object|null} props.selectedFile - The currently selected file object, or null if no file is selected.
 * @return {JSX.Element} The rendered JSX element for the file upload component.
 */
function FileUpload({ onFileSelect, selectedFile }) {
  const onDrop = useCallback(
    (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        onFileSelect(acceptedFiles[0]);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
    },
    multiple: false,
    maxSize: 10 * 1024 * 1024, // 10MB
  });

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="file-upload-container">
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'active' : ''} ${
          selectedFile ? 'has-file' : ''
        }`}
      >
        <input {...getInputProps()} />

        {selectedFile ? (
          <div className="file-info">
            <div className="file-icon">📄</div>
            <div className="file-details">
              <p className="file-name">{selectedFile.name}</p>
              <p className="file-size">{formatFileSize(selectedFile.size)}</p>
            </div>
            <button
              type="button"
              className="change-file-btn"
              onClick={(e) => {
                e.stopPropagation();
                onFileSelect(null);
              }}
            >
              Change File
            </button>
          </div>
        ) : (
          <div className="upload-prompt">
            <div className="upload-icon">📤</div>
            {isDragActive ? (
              <p className="upload-text">Drop the file here...</p>
            ) : (
              <>
                <p className="upload-text">
                  Drag & drop your spreadsheet here, or click to browse
                </p>
                <p className="upload-formats">
                  Accepted formats: CSV, XLSX, XLSM (Max 10MB)
                </p>
              </>
            )}
            <button type="button" className="browse-btn">
              Browse Files
            </button>
          </div>
        )}
      </div>

      {selectedFile && (
        <div className="file-requirements">
          <h4>Required Columns:</h4>
          <div className="columns-grid">
            <span className="column-item">Recipient First Name</span>
            <span className="column-item">Recipient Last Name</span>
            <span className="column-item">Recipient Email</span>
            <span className="column-item">Phone</span>
            <span className="column-item">Recipient Company Name</span>
            <span className="column-item">Address 1</span>
            <span className="column-item optional">Address 2 (Optional)</span>
            <span className="column-item">City</span>
            <span className="column-item">State</span>
            <span className="column-item">Zip</span>
            <span className="column-item">Country</span>
            <span className="column-item">Your Email</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default FileUpload;